import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useAddClientFilesMutation,
  useDeleteClientFilesMutation,
  useGetClientFileListQuery,
  useLazyGetClientFileQuery,
} from '@app/api/ClientFiles/api';
import type { ModelClientFile } from '@app/api/ClientFiles/types';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';

import type { BaseState } from './useBaseState';
import type { RcFile } from 'antd/es/upload/interface';

type UseLeadCardAddFileParams = Pick<
  BaseState,
  | 'leadCardData'
  | 'refetchLeadCard'
  | 'refreshLatestHistory'
  | 'selectedBranchId'
  | 'selectedLeadId'
>;

const ALLOWED_EXTENSIONS = [
  'xlsx',
  'xls',
  'jpg',
  'png',
  'jpeg',
  'pdf',
  'doc',
  'docx',
  'txt',
  'mp4',
  'webp',
  // MOV и DNG — как в общем списке загрузки (us/11171).
  'mov',
  'dng',
] as const;

export const LEAD_CARD_FILE_ALLOWED_EXTENSIONS: readonly string[] =
  ALLOWED_EXTENSIONS;
export const LEAD_CARD_FILE_MAX_SIZE_BYTES = 300 * 1024 * 1024;
export const LEAD_CARD_FILE_ACCEPTED_TYPES = ALLOWED_EXTENSIONS.map(
  (extension) => `.${extension}`
).join(',');

const getFileExtension = (fileName: string): string =>
  fileName.split('.').pop()?.toLowerCase() ?? '';

export const isAllowedFileExtension = (fileName: string): boolean =>
  ALLOWED_EXTENSIONS.includes(
    getFileExtension(fileName) as (typeof ALLOWED_EXTENSIONS)[number]
  );

export const isAllowedFileSize = (fileSize: number): boolean =>
  fileSize <= LEAD_CARD_FILE_MAX_SIZE_BYTES;

const readFileAsBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const parts = reader.result.split(',');
        const base64 = parts.length > 1 ? parts[1] : parts[0];
        resolve(base64);
      } else {
        reject(new Error('Не удалось прочитать файл'));
      }
    };

    reader.onerror = () => {
      reject(reader.error ?? new Error('Не удалось прочитать файл'));
    };

    reader.readAsDataURL(file);
  });

const base64ToBlob = (base64: string): Blob => {
  const binaryString = window.atob(base64);
  const { length } = binaryString;
  const bytes = new Uint8Array(length);

  for (let index = 0; index < length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return new Blob([bytes], { type: 'application/octet-stream' });
};

export const triggerBrowserDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface UseLeadCardAddFileResult {
  isFileModalOpen: boolean;
  isFileAvailable: boolean;
  files: ModelClientFile[];
  isFilesListLoading: boolean;
  isFilesListFetching: boolean;
  isUploadingFile: boolean;
  isRemovingFile: boolean;
  isDownloadingFile: boolean;
  openFileModal(): void;
  closeFileModal(): void;
  attachFile(file: RcFile): Promise<void>;
  downloadFile(file: ModelClientFile): Promise<void>;
  removeFile(fileId: number): Promise<void>;
}

export const useAddFile = ({
  leadCardData,
  refetchLeadCard,
  refreshLatestHistory,
  selectedBranchId,
  selectedLeadId,
}: UseLeadCardAddFileParams): UseLeadCardAddFileResult => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const [isFileModalOpen, setIsFileModalOpen] = useState(false);

  const clientId = leadCardData?.ClientId ?? leadCardData?.ClientView?.Id;
  const isFileAvailable = Boolean(clientId);

  const {
    data: filesData,
    isLoading: isFilesListLoading,
    isFetching: isFilesListFetching,
    refetch: refetchFiles,
    error: filesDataError,
    isError: isFilesDataError,
  } = useGetClientFileListQuery(
    { ActiveBranchId, clientId: clientId! },
    { skip: !isFileModalOpen || !clientId || !ActiveBranchId }
  );

  useErrorHandler([{ flag: isFilesDataError, error: filesDataError }]);

  const [addClientFiles, { isLoading: isUploadingFile }] =
    useAddClientFilesMutation();
  const [deleteClientFiles, { isLoading: isRemovingFile }] =
    useDeleteClientFilesMutation();
  const [getClientFile, { isFetching: isDownloadingFile }] =
    useLazyGetClientFileQuery();

  useEffect(() => {
    setIsFileModalOpen(false);
  }, [selectedBranchId, selectedLeadId]);

  const openFileModal = useCallback(() => {
    if (!clientId) {
      showNotification('Идентификатор клиента не найден', 'frontWarning');
      return;
    }

    setIsFileModalOpen(true);
  }, [clientId, showNotification]);

  const closeFileModal = useCallback(() => {
    if (isUploadingFile || isRemovingFile) return;

    setIsFileModalOpen(false);
  }, [isRemovingFile, isUploadingFile]);

  const attachFile = useCallback(
    async (file: RcFile) => {
      if (!clientId) {
        showNotification('Идентификатор клиента не найден', 'frontWarning');
        return;
      }

      if (!ActiveBranchId) {
        showNotification('Филиал не был распознан', 'frontWarning');
        return;
      }

      try {
        const data = await readFileAsBase64(file);

        await addClientFiles({
          ActiveBranchId,
          data: [
            {
              Id: 0,
              TimeCreated: '',
              ClientId: clientId,
              Description: null,
              FileName: file.name,
              FileSize: file.size,
              Data: data,
              Extension: getFileExtension(file.name),
              Thumbnail: null,
              ClientMessageId: null,
            },
          ],
        }).unwrap();

        showNotification('Файл успешно добавлен', 'success');
        refetchFiles();
        refetchLeadCard();
        void refreshLatestHistory();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      addClientFiles,
      clientId,
      refetchFiles,
      refetchLeadCard,
      refreshLatestHistory,
      showNotification,
    ]
  );

  const downloadFile = useCallback(
    async (file: ModelClientFile) => {
      if (!ActiveBranchId) {
        showNotification('Филиал не был распознан', 'frontWarning');
        return;
      }

      try {
        const fileData = file.Data
          ? file
          : await getClientFile({
              ActiveBranchId,
              fileId: file.Id,
            }).unwrap();

        if (!fileData.Data) {
          showNotification('Файл недоступен для скачивания', 'frontWarning');
          return;
        }

        const blob = base64ToBlob(fileData.Data);

        triggerBrowserDownload(blob, fileData.FileName ?? 'file');
      } catch (error) {
        showNotification(error);
      }
    },
    [ActiveBranchId, getClientFile, showNotification]
  );

  const removeFile = useCallback(
    async (fileId: number) => {
      if (!ActiveBranchId) {
        showNotification('Филиал не был распознан', 'frontWarning');
        return;
      }

      try {
        await deleteClientFiles({
          ActiveBranchId,
          data: [fileId],
        }).unwrap();

        showNotification('Файл удалён', 'success');
        refetchFiles();
        refetchLeadCard();
        void refreshLatestHistory();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      deleteClientFiles,
      refetchFiles,
      refetchLeadCard,
      refreshLatestHistory,
      showNotification,
    ]
  );

  const files = useMemo(() => filesData ?? [], [filesData]);

  return useMemo(
    () => ({
      isFileModalOpen,
      isFileAvailable,
      files,
      isFilesListLoading,
      isFilesListFetching,
      isUploadingFile,
      isRemovingFile,
      isDownloadingFile,
      openFileModal,
      closeFileModal,
      attachFile,
      downloadFile,
      removeFile,
    }),
    [
      attachFile,
      closeFileModal,
      downloadFile,
      files,
      isDownloadingFile,
      isFileAvailable,
      isFileModalOpen,
      isFilesListFetching,
      isFilesListLoading,
      isRemovingFile,
      isUploadingFile,
      openFileModal,
      removeFile,
    ]
  );
};
